import { ref } from "@vue/composition-api";
// @ts-ignore
import i18n from "@/libs/i18n";
import {
    ButtonCategory,
    ButtonType,
    ComponentHeaderType,
    TemplateCategory,
    TemplateInterface,
} from "@/interfaces/template.interface";
import useAssets from "@/composables/useAssets";

// https://developers.facebook.com/docs/whatsapp/api/messages/message-templates/
const metaSupportedTemplateLanguages: Array<{ name: string; shortCode: string }> = [
    { name: "Afrikaans", shortCode: "af" },
    { name: "Albanian", shortCode: "sq" },
    { name: "Arabic", shortCode: "ar" },
    { name: "Azerbaijani", shortCode: "az" },
    { name: "Bengali", shortCode: "bn" },
    { name: "Bulgarian", shortCode: "bg" },
    { name: "Catalan", shortCode: "ca" },
    { name: "Chinese (CHN)", shortCode: "zh_CN" },
    { name: "Chinese (HKG)", shortCode: "zh_HK" },
    { name: "Chinese (TAI)", shortCode: "zh_TW" },
    { name: "Chinese (CHN)", shortCode: "zh-CN" },
    { name: "Chinese (HKG)", shortCode: "zh-HK" },
    { name: "Chinese (TAI)", shortCode: "zh-TW" },
    { name: "Croatian", shortCode: "hr" },
    { name: "Czech", shortCode: "cs" },
    { name: "Danish", shortCode: "da" },
    { name: "Dutch", shortCode: "nl" },
    { name: "English", shortCode: "en" },
    { name: "English (UK)", shortCode: "en_GB" },
    { name: "English (US)", shortCode: "en_US" },
    { name: "English (UK)", shortCode: "en-GB" },
    { name: "English (US)", shortCode: "en-US" },
    { name: "Estonian", shortCode: "et" },
    { name: "Filipino", shortCode: "fil" },
    { name: "Finnish", shortCode: "fi" },
    { name: "French", shortCode: "fr" },
    { name: "Georgian", shortCode: "ka" },
    { name: "German", shortCode: "de" },
    { name: "Greek", shortCode: "el" },
    { name: "Gujarati", shortCode: "gu" },
    { name: "Hausa", shortCode: "ha" },
    { name: "Hebrew", shortCode: "he" },
    { name: "Hindi", shortCode: "hi" },
    { name: "Hungarian", shortCode: "hu" },
    { name: "Indonesian", shortCode: "id" },
    { name: "Irish", shortCode: "ga" },
    { name: "Italian", shortCode: "it" },
    { name: "Japanese", shortCode: "ja" },
    { name: "Kannada", shortCode: "kn" },
    { name: "Kazakh", shortCode: "kk" },
    { name: "Kinyarwanda", shortCode: "rw_RW" },
    { name: "Kinyarwanda", shortCode: "rw-RW" },
    { name: "Korean", shortCode: "ko" },
    { name: "Kyrgyz (Kyrgyzstan)", shortCode: "ky_KG" },
    { name: "Kyrgyz (Kyrgyzstan)", shortCode: "ky-KG" },
    { name: "Lao", shortCode: "lo" },
    { name: "Latvian", shortCode: "lv" },
    { name: "Lithuanian", shortCode: "lt" },
    { name: "Macedonian", shortCode: "mk" },
    { name: "Malay", shortCode: "ms" },
    { name: "Malayalam", shortCode: "ml" },
    { name: "Marathi", shortCode: "mr" },
    { name: "Norwegian", shortCode: "nb" },
    { name: "Persian", shortCode: "fa" },
    { name: "Polish", shortCode: "pl" },
    { name: "Portuguese (BR)", shortCode: "pt_BR" },
    { name: "Portuguese (POR)", shortCode: "pt_PT" },
    { name: "Portuguese (BR)", shortCode: "pt-BR" },
    { name: "Portuguese (POR)", shortCode: "pt-PT" },
    { name: "Punjabi", shortCode: "pa" },
    { name: "Romanian", shortCode: "ro" },
    { name: "Russian", shortCode: "ru" },
    { name: "Serbian", shortCode: "sr" },
    { name: "Slovak", shortCode: "sk" },
    { name: "Slovenian", shortCode: "sl" },
    { name: "Spanish", shortCode: "es" },
    { name: "Spanish (ARG)", shortCode: "es_AR" },
    { name: "Spanish (SPA)", shortCode: "es_ES" },
    { name: "Spanish (MEX)", shortCode: "es_MX" },
    { name: "Spanish (ARG)", shortCode: "es-AR" },
    { name: "Spanish (SPA)", shortCode: "es-ES" },
    { name: "Spanish (MEX)", shortCode: "es-MX" },
    { name: "Swahili", shortCode: "sw" },
    { name: "Swedish", shortCode: "sv" },
    { name: "Tamil", shortCode: "ta" },
    { name: "Telugu", shortCode: "te" },
    { name: "Thai", shortCode: "th" },
    { name: "Turkish", shortCode: "tr" },
    { name: "Ukrainian", shortCode: "uk" },
    { name: "Urdu", shortCode: "ur" },
    { name: "Uzbek", shortCode: "uz" },
    { name: "Vietnamese", shortCode: "vi" },
    { name: "Zulu", shortCode: "zu" },
];

// Add translation keys and map to translated select options
const messageTemplateLanguages = metaSupportedTemplateLanguages.map((language) => {
    const translationKey = language.name
        .replace(/ /g, "-")
        .replace(/[^a-z0-9-]/gi, "")
        .toLowerCase();

    return { value: language.shortCode, text: i18n.t(`locale.languages.${translationKey}`) };
});

const messageTemplateCategories = ref([
    { value: TemplateCategory.UTILITY, text: i18n.t("settings.templates.whatsapp.selections.categories.utility") },
    { value: TemplateCategory.MARKETING, text: i18n.t("settings.templates.whatsapp.selections.categories.marketing") },
]);

const headerTypeOptions = ref([
    { value: ComponentHeaderType.NONE, text: i18n.t("settings.templates.whatsapp.selections.none") },
    { value: ComponentHeaderType.TEXT, text: i18n.t("settings.templates.whatsapp.selections.headers.text") },
    { value: ComponentHeaderType.IMAGE, text: i18n.t("settings.templates.whatsapp.selections.headers.image") },
    { value: ComponentHeaderType.DOCUMENT, text: i18n.t("settings.templates.whatsapp.selections.headers.document") },
    { value: ComponentHeaderType.VIDEO, text: i18n.t("settings.templates.whatsapp.selections.headers.video") },
]);

const buttonTypeOptions = ref([
    { value: null, text: i18n.t("settings.templates.whatsapp.selections.none") },
    {
        value: ButtonCategory.CALL_TO_ACTION,
        text: i18n.t("settings.templates.whatsapp.selections.buttons.call-to-action"),
    },
    { value: ButtonCategory.REPLY, text: i18n.t("settings.templates.whatsapp.selections.buttons.quick-reply") },
]);

const callToActionButtonTypes = ref([
    { value: ButtonType.PHONE, text: i18n.t("settings.templates.whatsapp.selections.call-to-actions.phone") },
    { value: ButtonType.URL, text: i18n.t("settings.templates.whatsapp.selections.call-to-actions.url") },
]);

export default function useMessageTemplates() {
    const { getAsset } = useAssets();

    // @TODO create a interface for saysimple messages
    const convertTemplateToMessageContent = (template: TemplateInterface): Record<string, any> => {
        return {
            createdAt: new Date().toISOString(),
            text: getConvertedText(template),
            components: template.components,
            attachments: getConvertedAttachments(template),
        };
    };

    const getConvertedAttachments = (template: TemplateInterface): any => {
        if (!template.mediaType) {
            return undefined;
        }

        const category = template.mediaType.toLowerCase();

        if (template.assetId) {
            const asset = getAsset(template.assetId);

            if (asset) {
                return [asset];
            }
        }

        return (
            template.components.mediaExamples?.map((url) => ({
                category,
                url,
            })) ?? [
                {
                    category,
                    url: "https://mfe.saysimple.io/static/files/whatsapp-template-header-examples/image.jpg",
                },
            ]
        );
    };

    const getConvertedText = (template: TemplateInterface): string => {
        let text = template.content;

        if (!template.components.examples?.length) {
            return text;
        }

        let index = 0;

        text = text.replace(/\{\{[^{]+}}/g, (match) => template.components.examples?.[index++]?.trim() || match);

        return text;
    };

    const getLanguageFromShortCode = (shortCode: string): string => {
        return (
            messageTemplateLanguages.find((language) => language.value === shortCode)?.text ??
            i18n.t("locale.languages.unknown")
        );
    };

    return {
        convertTemplateToMessageContent,
        getLanguageFromShortCode,
        messageTemplateLanguages,
        messageTemplateCategories,
        headerTypeOptions,
        buttonTypeOptions,
        callToActionButtonTypes,
    };
}
